import { isDefined } from "@clipboard-health/util-ts";
import { RootPaths } from "@src/appV2/App/paths";
import { generatePath } from "react-router-dom";

import { RESUME_REQUIREMENT_ID } from "../PlacementCandidate/components/PlacementCandidateOnboarding/ResumeCard";

export const DOCUMENTS_BASE_PATH = "documents";
export const DOCUMENTS_BASE_PATH_FULL = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}`;

export const DOCUMENT_SEARCH_PATH = "search";
export const DOCUMENT_SEARCH_FULL_PATH = `${DOCUMENTS_BASE_PATH_FULL}/${DOCUMENT_SEARCH_PATH}`;

export const DOCUMENT_DETAILS_PATH = `:requirementId`;
export const DOCUMENT_DETAILS_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}`;

export const INDEPENDENT_CONTRACTOR_BADGE_PATH = "independent-contractor-badge";
export const INDEPENDENT_CONTRACTOR_BADGE_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${INDEPENDENT_CONTRACTOR_BADGE_PATH}`;

export const WORK_BADGE_REVIEW_POLICY_PATH = "work-badge-review-policy";
export const WORK_BADGE_REVIEW_POLICY_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${WORK_BADGE_REVIEW_POLICY_PATH}`;

export const WORKPLACE_DOCUMENTS_PATH = `workplace/:workplaceId`;
export const WORKPLACE_DOCUMENTS_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${WORKPLACE_DOCUMENTS_PATH}`;
export const WORKPLACE_DOCUMENTS_QUALIFICATION_PARAM = "qualification" as const;
export const WORKPLACE_DOCUMENTS_SHIFT_ID_PARAM = "shiftId" as const;
export const WORKPLACE_DOCUMENTS_POST_BOOKING_REQUIREMENTS_FILTER_PARAM =
  "postBookingRequirementsFilter" as const;

export type WorkplaceDocumentsPostBookingRequirementsFilter = "exclude" | "include-only";

interface BuildWorkplaceDocumentsPathParams {
  workplaceId: string;
  qualification?: string;
  shiftId?: string;
  postBookingRequirementsFilter?: WorkplaceDocumentsPostBookingRequirementsFilter;
}

export function buildWorkplaceDocumentsPath(params: BuildWorkplaceDocumentsPathParams): string {
  const { workplaceId, qualification, shiftId, postBookingRequirementsFilter } = params;

  const searchParams = new URLSearchParams();
  if (isDefined(qualification)) {
    searchParams.set(WORKPLACE_DOCUMENTS_QUALIFICATION_PARAM, qualification);
  }
  if (isDefined(shiftId)) {
    searchParams.set(WORKPLACE_DOCUMENTS_SHIFT_ID_PARAM, shiftId);
  }
  if (isDefined(postBookingRequirementsFilter)) {
    searchParams.set(
      WORKPLACE_DOCUMENTS_POST_BOOKING_REQUIREMENTS_FILTER_PARAM,
      postBookingRequirementsFilter
    );
  }

  const base = generatePath(WORKPLACE_DOCUMENTS_FULL_PATH, { workplaceId });
  const query = searchParams.toString();
  return query ? `${base}?${query}` : base;
}

export const DOCUMENT_PREVIEW_PATH = "upload-preview";
export const DOCUMENT_PREVIEW_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}/${DOCUMENT_PREVIEW_PATH}`;

export const DOCUMENT_UPLOAD_MODAL_PATH = "upload";
export const DOCUMENT_CAMERA_PERMISSION_ERROR_PATH = "camera-permission-error";

export const DOCUMENT_REQUIRED_BY_WORKPLACES_PATH = "required-by-workplaces";
export const DOCUMENT_REQUIRED_BY_WORKPLACES_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}/${DOCUMENT_REQUIRED_BY_WORKPLACES_PATH}`;

export const DOCUMENT_SUBMISSION_IN_PROGRESS_PATH = "submission-in-progress";
export const DOCUMENT_SUBMISSION_ERROR_PATH = "submission-error";
export const DOCUMENT_SUBMISSION_IN_REVIEW_PATH = "submission-in-review";
export const DOCUMENT_SUBMISSION_APPROVED_PATH = "submission-approved";
export const DOCUMENT_SUBMISSION_REJECTED_PATH = "submission-rejected";

export const DOCUMENT_TB_TEST_GUIDED_UPLOAD_PATH = "tb-test-guided-upload";
export const DOCUMENT_TB_TEST_GUIDED_UPLOAD_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}/${DOCUMENT_TB_TEST_GUIDED_UPLOAD_PATH}`;

export const DOCUMENT_SCHEDULE_TB_TEST_PATH = "schedule-tb-test";
export const DOCUMENT_SCHEDULE_TB_TEST_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}/${DOCUMENT_SCHEDULE_TB_TEST_PATH}`;

export const DOCUMENT_CHECKR_LOADING_PATH = "checkr-loading";
export const DOCUMENT_CHECKR_ERROR_PATH = "checkr-error";

export const DOCUMENT_DELETE_CONFIRMATION_PATH = "delete-confirmation/:documentId";
export const DOCUMENT_DELETE_CONFIRMATION_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}/${DOCUMENT_DELETE_CONFIRMATION_PATH}`;

export const DOCUMENT_STRIPE_VERIFICATION_SELECT_SUBTYPE_PATH =
  "stripe-verification-select-subtype";
export const DOCUMENT_STRIPE_VERIFICATION_SELECT_SUBTYPE_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}/${DOCUMENT_STRIPE_VERIFICATION_SELECT_SUBTYPE_PATH}`;

export const DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_PATH = "stripe-verification-in-progress";
export const DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}/${DOCUMENT_STRIPE_VERIFICATION_IN_PROGRESS_PATH}`;
export const DOCUMENT_STRIPE_VERIFICATION_REJECTED_PATH = "stripe-verification-rejected";
export const DOCUMENT_STRIPE_VERIFICATION_REJECTED_FULL_PATH = `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}/${DOCUMENT_STRIPE_VERIFICATION_REJECTED_PATH}`;

export const BACKGROUND_CHECK_BASE_PATH = "background-check";
export const BACKGROUND_CHECK_INTRO_PATH = "intro";
export const BACKGROUND_CHECK_DETAILS_PATH = "details";
export const BACKGROUND_CHECK_CONSENT_PATH = "consent";
export const BACKGROUND_CHECK_COMPLETED_PATH = "completed";

export const BACKGROUND_CHECK_INTRO_FULL_PATH = `${RootPaths.APP_V2_HOME}/${BACKGROUND_CHECK_BASE_PATH}/${BACKGROUND_CHECK_INTRO_PATH}`;

export const RESUME_DOCUMENT_REQUIREMENT_FULL_PATH = `${DOCUMENTS_BASE_PATH_FULL}/${RESUME_REQUIREMENT_ID}`;

export const NC_ORIENTATION_PATH = "nc-orientation";
export const NC_ORIENTATION_FULL_PATH = `${DOCUMENTS_BASE_PATH_FULL}/${NC_ORIENTATION_PATH}`;
